import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import AdminLayout from "../components/AdminLayout";
import { AdminApi } from "../api/admin-api";
import { TeamRoles, TeamRole } from "@repo/api/team-roles";
import { toast } from "sonner";

type FormData = {
  email: string;
  role: TeamRole;
  teamMode: "existing" | "new";
  existingTeamId: string;
  newTeamName: string;
  organizationId?: number;
};

export default function InviteUser() {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    role: TeamRoles.ADMIN,
    teamMode: "existing",
    existingTeamId: "",
    newTeamName: "",
    organizationId: undefined,
  });

  // Team name fetching state
  const [teamName, setTeamName] = useState<string>("");
  const [teamNameLoading, setTeamNameLoading] = useState(false);
  const [teamNameError, setTeamNameError] = useState<string>("");

  // Load organizations for team creation
  const { data: organizations, isLoading: loadingOrgs } = useQuery({
    queryKey: ["organizations"],
    queryFn: AdminApi.getAllOrganizations,
  });

  // Helper function for form updates
  const updateFormData = (updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Function to fetch team name by ID
  const fetchTeamName = async (teamId: string) => {
    const parsedTeamId = parseInt(teamId);
    if (isNaN(parsedTeamId) || parsedTeamId <= 0) {
      setTeamName("");
      setTeamNameError("");
      return;
    }

    setTeamNameLoading(true);
    setTeamNameError("");
    setTeamName("");

    try {
      const team = await AdminApi.getTeamById(parsedTeamId);
      setTeamName(team.name);
    } catch (error) {
      console.error("Failed to fetch team:", error);
      setTeamNameError("Team not found");
    } finally {
      setTeamNameLoading(false);
    }
  };

  // Handle team ID blur
  const handleTeamIdBlur = () => {
    if (formData.existingTeamId.trim()) {
      fetchTeamName(formData.existingTeamId.trim());
    } else {
      setTeamName("");
      setTeamNameError("");
    }
  };

  // Mutation for creating a new team
  const createTeamMutation = useMutation({
    mutationFn: AdminApi.createTeam,
    onError: (error) => {
      console.error("Failed to create team:", error);
      toast.error(`Failed to create team: ${error.message}`);
    },
  });

  // Mutation for creating an invite
  const createInviteMutation = useMutation({
    mutationFn: AdminApi.createInviteAsAdmin,
    onSuccess: (invite) => {
      toast.success(
        `Invite sent successfully to ${invite.email} for role ${invite.role}`,
      );
      // Reset form
      setFormData({
        email: "",
        role: TeamRoles.ADMIN,
        teamMode: "existing",
        existingTeamId: "",
        newTeamName: "",
        organizationId: undefined,
      });
      // Reset team name state
      setTeamName("");
      setTeamNameError("");
      setTeamNameLoading(false);
    },
    onError: (error) => {
      console.error("Failed to create invite:", error);
      toast.error(`Failed to create invite: ${error.message}`);
    },
  });

  // Validation functions
  const validateForm = (): string | null => {
    if (!formData.email) {
      return "Email is required";
    }

    if (formData.teamMode === "existing") {
      const parsedTeamId = parseInt(formData.existingTeamId);
      if (isNaN(parsedTeamId)) {
        return "Please enter a valid team ID number";
      }
    } else {
      if (!formData.newTeamName.trim()) {
        return "Team name is required";
      }
    }

    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    if (formData.teamMode === "existing") {
      // Use existing team
      const teamId = parseInt(formData.existingTeamId);
      createInviteMutation.mutate({
        email: formData.email,
        role: formData.role,
        teamId,
      });
    } else {
      // Create new team first, then invite
      createTeamMutation.mutate(
        {
          name: formData.newTeamName.trim(),
          organisationId: formData.organizationId,
        },
        {
          onSuccess: (newTeam) => {
            createInviteMutation.mutate({
              email: formData.email,
              role: formData.role,
              teamId: newTeam.id,
            });
          },
        },
      );
    }
  };

  const isSubmitting =
    createTeamMutation.isPending || createInviteMutation.isPending;

  return (
    <AdminLayout
      title="Invite User"
      description="Send an invite to a user to join an existing team or create a new team"
      breadcrumb="Invite User"
    >
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="user@example.com"
                required
              />
            </div>

            {/* Role Selection */}
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role
              </label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) =>
                  updateFormData({ role: e.target.value as TeamRole })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={TeamRoles.EDITOR}>Editor</option>
                <option value={TeamRoles.ADMIN}>Admin</option>
              </select>
            </div>

            {/* Team Selection Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Team Selection
              </label>
              <div className="space-y-4">
                {/* Existing Team Option */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="teamMode"
                      checked={formData.teamMode === "existing"}
                      onChange={() => {
                        updateFormData({ teamMode: "existing" });
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Use existing team
                    </span>
                  </label>
                  {formData.teamMode === "existing" && (
                    <div className="mt-2 ml-6">
                      <label
                        htmlFor="existingTeamId"
                        className="block text-sm text-gray-600 mb-1"
                      >
                        Team ID (internal database ID)
                      </label>
                      <input
                        type="number"
                        id="existingTeamId"
                        value={formData.existingTeamId}
                        onChange={(e) => {
                          updateFormData({ existingTeamId: e.target.value });
                          // Clear team name when typing
                          if (teamName || teamNameError) {
                            setTeamName("");
                            setTeamNameError("");
                          }
                        }}
                        onBlur={handleTeamIdBlur}
                        className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="123"
                        min="1"
                      />
                      {/* Team name display */}
                      {formData.teamMode === "existing" &&
                        formData.existingTeamId && (
                          <div className="mt-2">
                            {teamNameLoading && (
                              <div className="text-sm text-gray-500">
                                Loading team name...
                              </div>
                            )}
                            {teamName && (
                              <div className="text-sm text-green-700">
                                Team: {teamName}
                              </div>
                            )}
                            {teamNameError && (
                              <div className="text-sm text-red-600">
                                {teamNameError}
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>

                {/* New Team Option */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="teamMode"
                      checked={formData.teamMode === "new"}
                      onChange={() => {
                        updateFormData({ teamMode: "new" });
                        // Clear team name state when switching to new team mode
                        setTeamName("");
                        setTeamNameError("");
                        setTeamNameLoading(false);
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Create new team
                    </span>
                  </label>
                  {formData.teamMode === "new" && (
                    <div className="mt-2 ml-6 space-y-3">
                      <div>
                        <label
                          htmlFor="newTeamName"
                          className="block text-sm text-gray-600 mb-1"
                        >
                          Team Name
                        </label>
                        <input
                          type="text"
                          id="newTeamName"
                          value={formData.newTeamName}
                          onChange={(e) =>
                            updateFormData({ newTeamName: e.target.value })
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="My Team"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="organization"
                          className="block text-sm text-gray-600 mb-1"
                        >
                          Organization (optional)
                        </label>
                        {loadingOrgs ? (
                          <div className="text-sm text-gray-500">
                            Loading organizations...
                          </div>
                        ) : (
                          <select
                            id="organization"
                            value={formData.organizationId || ""}
                            onChange={(e) =>
                              updateFormData({
                                organizationId: e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">No organization</option>
                            {organizations?.map((org) => (
                              <option key={org.id} value={org.id}>
                                {org.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? "Sending Invite..." : "Send Invite"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
}
